import React, { Fragment, useState, useEffect } from 'react';
import { swotApi, usersApi, sectorsApi } from '../api';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { validateDateFields, extractApiError } from '../utils/dateValidation';
import {
  RefreshCw,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  Eye,
  TrendingUp,
  TrendingDown,
  Zap,
  Shield,
} from 'lucide-react';

const SWOT_TYPES = [
  { value: 'strength', label: 'Força', shortLabel: 'S', color: 'bg-green-500/20 border-green-500', textColor: 'text-green-400', headerBg: 'bg-green-600/30', icon: TrendingUp, actionLabel: 'Ações de Potencialização' },
  { value: 'weakness', label: 'Fraqueza', shortLabel: 'W', color: 'bg-red-500/20 border-red-500', textColor: 'text-red-400', headerBg: 'bg-red-600/30', icon: TrendingDown, actionLabel: 'Ações de Neutralização' },
  { value: 'opportunity', label: 'Oportunidade', shortLabel: 'O', color: 'bg-blue-500/20 border-blue-500', textColor: 'text-blue-400', headerBg: 'bg-blue-600/30', icon: Zap, actionLabel: 'Ações de Efetivação' },
  { value: 'threat', label: 'Ameaça', shortLabel: 'T', color: 'bg-yellow-500/20 border-yellow-500', textColor: 'text-yellow-400', headerBg: 'bg-yellow-600/30', icon: Shield, actionLabel: 'Ações de Neutralização' },
];

const ITEM_STATUS = [
  { value: 'active', label: 'Ativo' },
  { value: 'in_treatment', label: 'Em Tratamento' },
  { value: 'resolved', label: 'Resolvido' },
  { value: 'monitoring', label: 'Monitorando' },
];

// Prazo (deadline) helper: converts a target_date into a human deadline badge
const getDeadlineInfo = (targetDate) => {
  if (!targetDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(targetDate + 'T00:00:00');
  const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    return { text: `Atrasado há ${Math.abs(diffDays)} dia(s)`, color: 'text-red-400', days: diffDays };
  }
  if (diffDays === 0) {
    return { text: 'Vence hoje', color: 'text-orange-400', days: diffDays };
  }
  return { text: `${diffDays} dia(s) restantes`, color: diffDays <= 7 ? 'text-orange-400' : 'text-gray-400', days: diffDays };
};

const SWOT = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingAnalysis, setEditingAnalysis] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scope: '',
    period: '',
    sector_name: '',
    participants: '',
    responsible_id: '',
    review_date: '',
  });
  const [itemFormData, setItemFormData] = useState({
    swot_type: 'strength',
    title: '',
    description: '',
    importance: 3,
    urgency: 3,
    related_process: '',
    related_area: '',
    external_factor: '',
    action_text: '',
    responsible_id: '',
    target_date: '',
  });
  const [deadlineDays, setDeadlineDays] = useState('');
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [sectors, setSectors] = useState([]);

  useEffect(() => {
    loadData();
    loadUsers();
    loadSectors();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [analysesRes, statsRes] = await Promise.all([
        swotApi.list(),
        swotApi.stats(),
      ]);
      setAnalyses(analysesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadSectors = async () => {
    try {
      const response = await sectorsApi.listActive();
      setSectors(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ is_active: true });
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadItems = async (analysisId) => {
    try {
      const res = await swotApi.listItems(analysisId);
      setItems(res.data);
    } catch (error) {
      console.error('Error loading items:', error);
    }
  };

  const handleOpenModal = async (analysis = null) => {
    if (analysis) {
      try {
        const res = await swotApi.get(analysis.id);
        const fullAnalysis = res.data;
        setEditingAnalysis(fullAnalysis);
        setFormData({
          title: fullAnalysis.title,
          description: fullAnalysis.description || '',
          scope: fullAnalysis.scope || '',
          period: fullAnalysis.period || '',
          sector_name: fullAnalysis.sector_name || '',
          participants: fullAnalysis.participants || '',
          responsible_id: fullAnalysis.responsible_id || '',
          review_date: fullAnalysis.review_date || '',
        });
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados da análise SWOT');
      }
    } else {
      setEditingAnalysis(null);
      setFormData({
        title: '',
        description: '',
        scope: '',
        period: '',
        sector_name: '',
        participants: '',
        responsible_id: '',
        review_date: '',
      });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAnalysis(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Preencha o título');
      return;
    }

    const dateError = validateDateFields([
      { value: formData.review_date, label: 'Data de Revisão' },
    ]);
    if (dateError) {
      toast.error(dateError);
      return;
    }
    
    setSaving(true);
    
    try {
      const data = {
        ...formData,
        responsible_id: formData.responsible_id || null,
        review_date: formData.review_date || null,
      };

      if (editingAnalysis) {
        await swotApi.update(editingAnalysis.id, data);
        toast.success('Análise atualizada!');
      } else {
        await swotApi.create(data);
        toast.success('Análise criada!');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (analysis) => {
    if (!window.confirm(`Excluir análise "${analysis.code}"?`)) return;
    
    try {
      await swotApi.delete(analysis.id);
      toast.success('Análise excluída!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleViewDetails = async (analysis) => {
    try {
      const res = await swotApi.get(analysis.id);
      setSelectedAnalysis(res.data);
      await loadItems(analysis.id);
      setExpandedQuadrant(null);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleOpenItemModal = (type = 'strength') => {
    setItemFormData({
      swot_type: type,
      title: '',
      description: '',
      importance: 3,
      urgency: 3,
      related_process: '',
      related_area: '',
      external_factor: '',
      action_text: '',
      responsible_id: '',
      target_date: '',
    });
    setDeadlineDays('');
    setShowItemModal(true);
  };

  // "Tenho 15 dias para..." — preenche o Prazo a partir de uma quantidade de dias
  const handleDeadlineDaysChange = (value) => {
    setDeadlineDays(value);
    const days = parseInt(value, 10);
    if (!value || isNaN(days)) {
      setItemFormData((prev) => ({ ...prev, target_date: '' }));
      return;
    }
    const date = new Date();
    date.setDate(date.getDate() + days);
    setItemFormData((prev) => ({ ...prev, target_date: date.toISOString().split('T')[0] }));
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();

    if (!itemFormData.title) {
      toast.error('Preencha o título');
      return;
    }

    const dateError = validateDateFields([
      { value: itemFormData.target_date, label: 'Prazo' },
    ]);
    if (dateError) {
      toast.error(dateError);
      return;
    }

    setSaving(true);

    try {
      const data = {
        ...itemFormData,
        responsible_id: itemFormData.responsible_id || null,
        target_date: itemFormData.target_date || null,
      };
      await swotApi.createItem(selectedAnalysis.id, data);
      toast.success('Item adicionado!');
      setShowItemModal(false);
      await loadItems(selectedAnalysis.id);
      // Refresh analysis to get updated counts
      const res = await swotApi.get(selectedAnalysis.id);
      setSelectedAnalysis(res.data);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao adicionar'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Excluir este item?')) return;
    
    try {
      await swotApi.deleteItem(itemId);
      toast.success('Item excluído!');
      await loadItems(selectedAnalysis.id);
      const res = await swotApi.get(selectedAnalysis.id);
      setSelectedAnalysis(res.data);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const filteredAnalyses = analyses.filter(a => {
    const matchesSearch =
      a.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.scope?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment =
      departmentFilter === 'all' || a.sector_name === departmentFilter;
    return matchesSearch && matchesDepartment;
  });

  const getItemsByType = (type) => items.filter(i => i.swot_type === type);

  // Quadrante em destaque: clicar no cabeçalho de "Força"/"Fraqueza"/etc.
  // expande aquele quadrante pra largura toda do modal e aumenta a área
  // visível da tabela, em vez de deixar tudo espremido em 2 colunas.
  // Clicar de novo (ou em outro quadrante) recolhe/troca o destaque.
  const [expandedQuadrant, setExpandedQuadrant] = useState(null);

  const SWOTQuadrant = ({ type, typeConfig, items, expanded, onToggleExpand }) => (
    <div className={`${typeConfig.color} border rounded-lg overflow-hidden flex flex-col transition-all ${expanded ? 'md:col-span-2' : ''}`}>
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex justify-between items-center px-4 py-3 text-left hover:bg-black/10 transition-colors"
        title={expanded ? 'Recolher' : 'Expandir'}
      >
        <h3 className={`font-semibold ${typeConfig.textColor} flex items-center gap-2`}>
          <typeConfig.icon size={20} />
          {typeConfig.label}
          <span className="text-gray-400 text-sm">({items.length})</span>
        </h3>
        <span
          onClick={(e) => { e.stopPropagation(); handleOpenItemModal(type); }}
          className="p-1 hover:bg-gray-700 rounded cursor-pointer"
          title="Adicionar"
        >
          <Plus size={16} className={typeConfig.textColor} />
        </span>
      </button>
      {/* Tabela no formato da planilha (Item / Ações / Gestor / Prazo).
          Quando o quadrante está em destaque, a área da tabela cresce
          bastante em vez de ficar limitada aos ~18rem padrão. */}
      <div className={`overflow-x-auto overflow-y-auto ${expanded ? 'max-h-[32rem]' : 'max-h-72'}`}>
        {items.length === 0 ? (
          <p className="text-gray-500 text-sm italic px-4 pb-3">Nenhum item</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className={typeConfig.headerBg}>
                <th className="text-left px-3 py-2 text-white font-medium">Item</th>
                <th className="text-left px-3 py-2 text-white font-medium">{typeConfig.actionLabel}</th>
                <th className="text-left px-3 py-2 text-white font-medium">Gestor</th>
                <th className="text-left px-3 py-2 text-white font-medium">Prazo</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const deadline = getDeadlineInfo(item.target_date);
                return (
                  <tr key={item.id} className="border-t border-gray-700/50 align-top group">
                    <td className="px-3 py-2 min-w-[160px]">
                      <span className="text-white font-medium">{item.title}</span>
                      {item.description && (
                        <p className="text-gray-400 text-xs mt-0.5">{item.description}</p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-300 text-xs min-w-[220px]">
                      {item.action_text || <span className="text-gray-600 italic">-</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-300 text-xs whitespace-nowrap">
                      {item.responsible_name || <span className="text-gray-600 italic">-</span>}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {deadline ? (
                        <span className={deadline.color}>{deadline.text}</span>
                      ) : (
                        <span className="text-gray-600 italic">-</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-400 transition-all"
                        title="Excluir"
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (analysis) => <td className="font-mono text-green-400">{analysis.code}</td> },
    { key: 'title', label: 'Título', render: (analysis) => <td><div className="max-w-xs truncate">{analysis.title}</div></td> },
    { key: 'edition', label: 'Edição', render: (analysis) => <td>{analysis.scope || '-'}</td> },
    { key: 'period', label: 'Período', render: (analysis) => <td>{analysis.period || '-'}</td> },
    { key: 's', label: 'S', headerClass: 'text-center', render: (analysis) => <td className="text-center text-green-400 font-semibold">{analysis.strengths_count}</td> },
    { key: 'w', label: 'W', headerClass: 'text-center', render: (analysis) => <td className="text-center text-red-400 font-semibold">{analysis.weaknesses_count}</td> },
    { key: 'o', label: 'O', headerClass: 'text-center', render: (analysis) => <td className="text-center text-blue-400 font-semibold">{analysis.opportunities_count}</td> },
    { key: 't', label: 'T', headerClass: 'text-center', render: (analysis) => <td className="text-center text-yellow-400 font-semibold">{analysis.threats_count}</td> },
    { key: 'status', label: 'Status', render: (analysis) => (
      <td>
        <span className={`badge ${analysis.status === 'active' ? 'badge-green' : 'badge-gray'}`}>
          {analysis.status === 'active' ? 'Ativa' : analysis.status === 'draft' ? 'Rascunho' : 'Arquivada'}
        </span>
      </td>
    ) },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (analysis) => (
      <td className="text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => handleViewDetails(analysis)}
            className="p-1 hover:text-blue-400 transition-colors"
            title="Ver matriz"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => handleOpenModal(analysis)}
            className="p-1 hover:text-green-400 transition-colors"
            title="Editar"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDelete(analysis)}
            className="p-1 hover:text-red-400 transition-colors"
            title="Excluir"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'swot', columns: COLUMNS });

  return (
    <div className="space-y-6" data-testid="swot-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Análise SWOT</h1>
          <p className="text-gray-400 text-sm">Forças, Fraquezas, Oportunidades e Ameaças</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary"
          data-testid="new-swot-btn"
        >
          <Plus size={20} />
          Nova Análise
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Análises</p>
            <p className="text-2xl font-bold text-white">{stats.total_analyses}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Ativas</p>
            <p className="text-2xl font-bold text-green-500">{stats.active_analyses}</p>
          </div>
          <div className="card p-4 border-l-2 border-green-500">
            <p className="text-gray-400 text-sm">Forças</p>
            <p className="text-2xl font-bold text-green-400">{stats.total_strengths}</p>
          </div>
          <div className="card p-4 border-l-2 border-red-500">
            <p className="text-gray-400 text-sm">Fraquezas</p>
            <p className="text-2xl font-bold text-red-400">{stats.total_weaknesses}</p>
          </div>
          <div className="card p-4 border-l-2 border-blue-500">
            <p className="text-gray-400 text-sm">Oportunidades</p>
            <p className="text-2xl font-bold text-blue-400">{stats.total_opportunities}</p>
          </div>
          <div className="card p-4 border-l-2 border-yellow-500">
            <p className="text-gray-400 text-sm">Ameaças</p>
            <p className="text-2xl font-bold text-yellow-400">{stats.total_threats}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por código, título ou edição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
              data-testid="search-input"
            />
          </div>
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="input sm:w-56"
            data-testid="department-filter"
          >
            <option value="all">Todos os departamentos</option>
            {sectors.map(s => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </select>
          <button onClick={loadData} className="btn-secondary" data-testid="refresh-btn">
            <RefreshCw size={20} />
          </button>
          <div className="flex items-center">
            <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : filteredAnalyses.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p>Nenhuma análise SWOT encontrada</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAnalyses.map((analysis) => (
                  <tr key={analysis.id} data-testid={`swot-row-${analysis.id}`}>
                    {visibleColumns.map(col => (
                      <Fragment key={col.key}>{col.render(analysis)}</Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingAnalysis ? 'Editar Análise SWOT' : 'Nova Análise SWOT'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="input w-full"
                  placeholder="Ex: Análise SWOT 2026"
                  data-testid="swot-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Descrição da análise"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Edição</label>
                  <input
                    type="text"
                    value={formData.scope}
                    onChange={(e) => setFormData({...formData, scope: e.target.value})}
                    className="input w-full"
                    placeholder="Ex: Ed. 01, Revisão 02..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Departamento</label>
                  <select
                    value={formData.sector_name}
                    onChange={(e) => setFormData({...formData, sector_name: e.target.value})}
                    className="input w-full"
                  >
                    <option value="">Selecione um departamento</option>
                    {sectors.map(s => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nome do Gestor</label>
                  <select
                    value={formData.responsible_id}
                    onChange={(e) => setFormData({...formData, responsible_id: e.target.value})}
                    className="input w-full"
                  >
                    <option value="">Selecione...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Período</label>
                  <input
                    type="text"
                    value={formData.period}
                    onChange={(e) => setFormData({...formData, period: e.target.value})}
                    className="input w-full"
                    placeholder="Ex: 2026-Q1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nome dos Participantes da Análise</label>
                <textarea
                  value={formData.participants}
                  onChange={(e) => setFormData({...formData, participants: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Ex: João Silva, Maria Souza, Carlos Lima..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Data de Revisão</label>
                <input
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => setFormData({...formData, review_date: e.target.value})}
                  className="input w-full"
                  min="1900-01-01"
                  max="2099-12-31"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving} data-testid="swot-submit-btn">
                  {saving ? <Loader2 className="animate-spin" size={18} /> : editingAnalysis ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Detail/Matrix Modal */}
      {showDetailModal && selectedAnalysis && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content modal-content-wide p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedAnalysis.code} - {selectedAnalysis.title}</h2>
                <p className="text-gray-400 text-sm">{selectedAnalysis.scope ? `Edição: ${selectedAnalysis.scope}` : ''}{selectedAnalysis.scope && selectedAnalysis.period ? ' | ' : ''}{selectedAnalysis.period}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Header info (Gestor / Departamento / Participantes) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-sm">
              <div>
                <p className="text-gray-400 text-xs uppercase mb-1">Nome do Gestor</p>
                <p className="text-white">{selectedAnalysis.responsible_name || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase mb-1">Departamento</p>
                <p className="text-white">{selectedAnalysis.sector_name || '-'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase mb-1">Participantes da Análise</p>
                <p className="text-white">{selectedAnalysis.participants || '-'}</p>
              </div>
            </div>

            {/* SWOT Matrix */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SWOT_TYPES.map(type => (
                <SWOTQuadrant
                  key={type.value}
                  type={type.value}
                  typeConfig={type}
                  items={getItemsByType(type.value)}
                  expanded={expandedQuadrant === type.value}
                  onToggleExpand={() => setExpandedQuadrant(prev => prev === type.value ? null : type.value)}
                />
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-700">
              <button onClick={() => setShowDetailModal(false)} className="btn-secondary">
                Fechar
              </button>
              <button 
                onClick={() => { 
                  const res = swotApi.update(selectedAnalysis.id, { status: 'active' });
                  toast.success('Análise ativada!');
                  loadData();
                }} 
                className="btn-primary"
              >
                Ativar Análise
              </button>
            </div>
          </div>
        </>
      )}

      {/* Add Item Modal — abre por cima do modal de detalhe (que já está
          aberto), então precisa de z-index maior que o padrão (backdrop
          z-40 / content z-50): senão os dois ficam no mesmo "andar" da
          pilha de camadas e o conteúdo do modal de detalhe (z-50) aparece
          por cima do backdrop deste aqui (z-40), sem escurecer nada atrás.
          Usa style inline porque .modal-backdrop/.modal-content já fixam
          z-index via CSS próprio, que venceria uma classe zIndex normal. */}
      {showItemModal && (
        <>
          <div className="modal-backdrop" style={{ zIndex: 60 }} />
          <div className="modal-content p-6 max-w-lg" style={{ zIndex: 70 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                Adicionar {SWOT_TYPES.find(t => t.value === itemFormData.swot_type)?.label}
              </h2>
              <button onClick={() => setShowItemModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmitItem} className="space-y-4">
              {/* O tipo já foi definido ao clicar em "+" no quadrante certo
                  (título do modal reflete isso), então não precisa de campo
                  aqui — segue a planilha de referência: Item, Ações, Gestor, Prazo. */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Item *</label>
                <input
                  type="text"
                  value={itemFormData.title}
                  onChange={(e) => setItemFormData({...itemFormData, title: e.target.value})}
                  className="input w-full"
                  placeholder="Título do item"
                  data-testid="item-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {SWOT_TYPES.find(t => t.value === itemFormData.swot_type)?.actionLabel}
                </label>
                <textarea
                  value={itemFormData.action_text}
                  onChange={(e) => setItemFormData({...itemFormData, action_text: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Ação para potencializar/neutralizar/efetivar este item"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Gestor</label>
                  <select
                    value={itemFormData.responsible_id}
                    onChange={(e) => setItemFormData({...itemFormData, responsible_id: e.target.value})}
                    className="input w-full"
                  >
                    <option value="">Selecione...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Prazo</label>
                  <input
                    type="date"
                    value={itemFormData.target_date}
                    onChange={(e) => { setItemFormData({...itemFormData, target_date: e.target.value}); setDeadlineDays(''); }}
                    className="input w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Ou defina o prazo em dias <span className="text-gray-500 font-normal">(ex: 15 dias)</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={deadlineDays}
                  onChange={(e) => handleDeadlineDaysChange(e.target.value)}
                  className="input w-full"
                  placeholder="Ex: 15"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={() => setShowItemModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving} data-testid="item-submit-btn">
                  {saving ? <Loader2 className="animate-spin" size={18} /> : 'Adicionar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default SWOT;
